import { PaginationParamsModel } from '@snap-types/models/pagination';
import { isEmpty, isMatch, merge, omit } from 'lodash-es';
import qs from 'qs';

const PAGINATION_DEFAULT_PARAM_KEYS = Object.keys(
  PaginationParamsModel.defaultParams,
);

export type SearchParams = Record<
  string,
  string | number | boolean | undefined | null
>;

/**
 * Append search parameters to a URL string
 *
 * @param baseUrl - The base URL string
 * @param params - Object containing key-value pairs to append as search parameters
 * @returns The URL string with appended search parameters
 */
export function withSearchParams(
  baseUrl: string,
  params: SearchParams | null,
): string {
  const url = new URL(baseUrl, 'http://localhost'); // Use dummy base for relative URLs

  const urlWithPathname = baseUrl.startsWith('http')
    ? url.toString()
    : url.pathname;

  const allParams = merge({}, qs.parse(url.search.slice(1)), params);

  if (!isEmpty(allParams)) {
    return `${urlWithPathname}?${qs.stringify(allParams, {
      skipNulls: true,
    })}`;
  }

  return urlWithPathname;
}

/**
 * Check if a current URL matches a reference URL, with optional search parameter matching
 *
 * Path matching:
 * - Always checks if pathnames are identical
 *
 * Search parameter matching (when matchSearch is true):
 * - Pagination params (page, perPage, etc.) are always ignored in comparisons
 * - If reference has no non-pagination params:
 *   - Returns true only if current also has no non-pagination params
 * - If reference has non-pagination params:
 *   - Returns true if current contains all reference non-pagination params
 *   - Uses partial matching: current URL can have additional params beyond reference params
 *
 * Search parameter matching (when matchSearch is false):
 * - Only checks pathname, ignores all search parameters
 *
 * @param current - The current URL to check
 * @param reference - The reference URL to match against
 * @param matchSearch - Whether to include search parameter matching
 * @returns True if URLs match according to the rules above
 *
 * @example
 * ```ts
 * const current = new URL('/quotes?status=active&page=2', 'http://localhost');
 * const ref = new URL('/quotes?status=active', 'http://localhost');
 *
 * isURLMatch(current, ref, true);  // true - current contains all ref params
 * isURLMatch(current, ref, false); // true - paths match, search ignored
 *
 * const refNoParams = new URL('/quotes', 'http://localhost');
 * const currentNoParams = new URL('/quotes?page=2', 'http://localhost');
 * isURLMatch(currentNoParams, refNoParams, true);  // true - both have no non-pagination params
 *
 * const currentWithParams = new URL('/quotes?status=active', 'http://localhost');
 * isURLMatch(currentWithParams, refNoParams, true);  // false - current has non-pagination params
 * ```
 */
export function isURLMatch(
  current: URL,
  reference: URL,
  matchSearch: boolean,
): boolean {
  const isPathMatch = current.pathname === reference.pathname;

  if (matchSearch) {
    const currentParams = qs.parse(current.search.slice(1));
    const refParams = qs.parse(reference.search.slice(1));

    if (isEmpty(omit(refParams, PAGINATION_DEFAULT_PARAM_KEYS))) {
      return (
        isPathMatch &&
        isEmpty(omit(currentParams, PAGINATION_DEFAULT_PARAM_KEYS))
      );
    }

    return (
      isPathMatch &&
      isMatch(
        omit(currentParams, PAGINATION_DEFAULT_PARAM_KEYS),
        omit(refParams, PAGINATION_DEFAULT_PARAM_KEYS),
      )
    );
  }

  return isPathMatch;
}
